import React, { useEffect } from 'react';
import Hero from '../components/Hero';
import Marquee from '../components/Marquee';
import LosingClients from '../components/LosingClients';
import Services from '../components/Services';
import Results from '../components/Results';
import WhatWeEnable from '../components/WhatWeEnable';
import SimpleProcess from '../components/SimpleProcess';
import Testimonials from '../components/Testimonials';
import Pricing from '../components/Pricing';
import Visits from '../components/Visits';
import FinalCTA from '../components/FinalCTA'; 
import GrowthPartners from '../components/GrowthPartners';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';

const HomePage = () => {
  const { t } = useTranslation();
  // Add IDs to sections after component mounts
  useEffect(() => {
    // Add a small delay to ensure components are rendered
    setTimeout(() => {
      const sections = [
        { className: 'hero-section', id: 'hero' },
        { className: 'what-we-enable', id: 'whatweenable' },
        { className: 'services', id: 'services' },
        { className: 'results', id: 'results' },
        { className: 'simple-process', id: 'process' },
        { className: 'growth-partners', id: 'growth-partners' },
        { className: 'testimonials', id: 'testimonials' },
        { className: 'pricing', id: 'pricing' },
        { className: 'final-cta', id: 'contact' }
      ];
      
      sections.forEach(section => {
        const element = document.querySelector(`.${section.className}`);
        if (element) {
          element.id = section.id;
        }
      });
    }, 100);
  }, []);
  
  return (
    <>
      <Helmet>
        <title>{t('homeMetaTitle')}</title>
        <meta name="description" content="Enzlo ist Ihr digitaler Wachstumspartner für Webdesign, SEO und Google Ads. Wir helfen lokalen Unternehmen in Deutschland, online mehr Kunden zu gewinnen." />
        <link rel="canonical" href="https://enzlo.com/" />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://enzlo.com/" />
        <meta property="og:title" content={t('homeMetaTitle')} />
        <meta property="og:description" content={t('homeMetaDescription')} />
        <meta property="og:image" content="https://enzlo.com/images/og-image-enzlo.png" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@enzlo_com" />
        <meta name="twitter:creator" content="@franziskaspo" />
        <meta name="twitter:title" content={t('homeMetaTitle')} />
        <meta name="twitter:description" content={t('homeMetaDescription')} />
        <meta name="twitter:image" content="https://enzlo.com/images/og-image-enzlo.png" />
        <meta property="og:site_name" content="Enzlo" />
        <meta property="og:see_also" content="https://www.facebook.com/enzloinc/" />
        <meta property="og:see_also" content="https://www.youtube.com/@Totalemailautoresponder" />
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Spoddig UG (haftungsbeschränkt)",
            "legalName": "Spoddig UG (haftungsbeschränkt), HRB 234397 B",
            "url": "https://enzlo.com",
            "logo": "https://enzlo.com/assets/logo.png",
            "telephone": "+4915751619973",
            "address": [
              {
                "@type": "PostalAddress",
                "streetAddress": "Eckweg 8",
                "postalCode": "64354",
                "addressLocality": "Reinheim",
                "addressCountry": "DE"
              },
              {
                "@type": "PostalAddress",
                "streetAddress": "Zeppelinstraße 20",
                "postalCode": "73084",
                "addressLocality": "Salach",
                "addressCountry": "DE"
              }
            ],
            "sameAs": [
              "https://www.facebook.com/enzloinc/",
              "https://www.youtube.com/@Totalemailautoresponder"
            ],
            "description": "Digital growth partner for local businesses in Germany and the US, specializing in SEO, Google Ads, and web development."
          }
          `}
        </script>
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Enzlo Frankfurt Region",
            "parentOrganization": {
              "@type": "Organization",
              "name": "Spoddig UG (haftungsbeschränkt)"
            },
            "image": "https://enzlo.com/assets/logo.png",
            "telephone": "+4915751619973",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Eckweg 8",
              "postalCode": "64354",
              "addressLocality": "Reinheim",
              "addressCountry": "DE"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 49.8293,
              "longitude": 8.8351
            },
            "url": "https://enzlo.com/",
            "openingHours": "Mo-Fr 09:00-18:00"
          }
          `}
        </script>
        <script type="application/ld+json">
          {`
          {
            "@context": "https://schema.org",
            "@type": "LocalBusiness",
            "name": "Enzlo Stuttgart/Ulm Region",
            "parentOrganization": {
              "@type": "Organization",
              "name": "Spoddig UG (haftungsbeschränkt)"
            },
            "image": "https://enzlo.com/assets/logo.png",
            "telephone": "+4915751619973",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "Zeppelinstraße 20",
              "postalCode": "73084",
              "addressLocality": "Salach",
              "addressCountry": "DE"
            },
            "geo": {
              "@type": "GeoCoordinates",
              "latitude": 48.6932,
              "longitude": 9.7034
            },
            "url": "https://enzlo.com/",
            "openingHours": "Mo-Fr 09:00-18:00"
          }
          `}
        </script>
        <script type="application/ld+json">{`
        [
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Webdesign Agentur",
            "description": "Professionelle Websites für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm und Umgebung.",
            "provider": {
              "@type": "Organization",
              "name": "Spoddig UG (haftungsbeschränkt)"
            },
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "SEO Agentur",
            "description": "Suchmaschinenoptimierung (SEO) für lokale Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "provider": {
              "@type": "Organization",
              "name": "Spoddig UG (haftungsbeschränkt)"
            },
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Google Ads Agentur",
            "description": "Google Ads Kampagnen für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "provider": {
              "@type": "Organization",
              "name": "Spoddig UG (haftungsbeschränkt)"
            },
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Online Buchungssystem",
            "description": "Online-Buchungslösungen für Dienstleister und Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "provider": {
              "@type": "Organization",
              "name": "Spoddig UG (haftungsbeschränkt)"
            },
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Reputation Management",
            "description": "Automatisierte Generierung von Google Bewertungen für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "provider": {
              "@type": "Organization",
              "name": "Spoddig UG (haftungsbeschränkt)"
            },
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          },
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "E-Mail Marketing",
            "description": "E-Mail Marketing und Newsletter für Unternehmen in Frankfurt, Darmstadt, Aschaffenburg, Stuttgart, Ulm.",
            "provider": {
              "@type": "Organization",
              "name": "Spoddig UG (haftungsbeschränkt)"
            },
            "areaServed": ["Frankfurt", "Darmstadt", "Aschaffenburg", "Stuttgart", "Ulm"]
          }
        ]
        `}</script>
      </Helmet>
      <main>
        <Hero />
        <Marquee />
        <LosingClients />
        <WhatWeEnable />
        <Services />
        <Results />
        <SimpleProcess />
        <GrowthPartners />
        <Testimonials />
        <Pricing />
        <Visits />
        <FinalCTA />
      </main>
    </>
  );
};

export default HomePage;
